import React, { useState, useEffect, useRef } from 'react';
import { X, RotateCcw, HelpCircle, Star, Sparkles, Sun, Droplets, Wind, Shuffle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { areAdjacent, findMatches, applyGravity, checkSpecialMatch, hasPossibleMoves } from '../utils/gameEngine';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ||  '';

const LoveGardenGame = ({ levelNumber, onClose, onComplete, token }) => {
  // Game state
  const [board, setBoard] = useState(null);
  const [levelConfig, setLevelConfig] = useState(null);
  const [movesLeft, setMovesLeft] = useState(0);
  const [score, setScore] = useState(0);
  const [goals, setGoals] = useState([]);
  const [selectedTile, setSelectedTile] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [gameState, setGameState] = useState('loading'); // loading, playing, tutorial, won, lost
  const [tutorialStep, setTutorialStep] = useState(0);
  const [comboCount, setComboCount] = useState(0);
  const [cascadeCount, setCascadeCount] = useState(0);
  const [boosters, setBoosters] = useState({ sunbeam: 0, waterfall: 0, breeze: 0 });
  const [selectedBooster, setSelectedBooster] = useState(null);
  const [forceRender, setForceRender] = useState(0); // Force re-render counter
  const [showNoMovesModal, setShowNoMovesModal] = useState(false);
  const [invalidSwapTile, setInvalidSwapTile] = useState(null); // For shake animation
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [hintsLeft, setHintsLeft] = useState(1); // 1 free hint per level
  const [hintedTiles, setHintedTiles] = useState([]); // Tiles to highlight
  const [showHintGlow, setShowHintGlow] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showXPConfirm, setShowXPConfirm] = useState(false);
  const [userXP, setUserXP] = useState(0);
  
  // Stats for end screen
  const [stats, setStats] = useState({
    maxCombo: 0,
    maxCascade: 0,
    specialsCreated: 0,
    specialsUsed: 0,
    blockersCleared: 0
  });

  const boardRef = useRef(null);
  const startTime = useRef(Date.now());
  const isAnimatingRef = useRef(false);
  const isProcessingRef = useRef(false); // Additional flag to prevent double-processing

  // Sync ref with state AND force re-render after state changes
  useEffect(() => {
    isAnimatingRef.current = isAnimating;
    console.log('🔄 isAnimating changed to:', isAnimating);
    
    // Force a re-render to ensure UI updates
    if (!isAnimating) {
      setForceRender(prev => prev + 1);
    }
  }, [isAnimating]);

  // Load level data
  useEffect(() => {
    loadLevel();
  }, [levelNumber]);
  
  // Failsafe: Reset animation flag if stuck for more than 2 seconds
  useEffect(() => {
    if (isAnimating) {
      console.log('⏱️ Animation started, setting 2s timeout');
      const timeout = setTimeout(() => {
        console.error('🚨 ANIMATION STUCK FOR 2s - FORCE RESETTING ALL FLAGS');
        isAnimatingRef.current = false;
        isProcessingRef.current = false;
        setIsAnimating(false);
        setForceRender(prev => prev + 1);
        
        // Check for no moves after unstuck
        const hasMoves = hasPossibleMoves(board);
        if (!hasMoves && movesLeft > 0) {
          console.log('⚠️ Board stuck with no moves - showing modal');
          setTimeout(() => setShowNoMovesModal(true), 100);
        }
      }, 2000);
      return () => {
        console.log('⏱️ Animation ended, clearing timeout');
        clearTimeout(timeout);
      };
    }
  }, [isAnimating, board, movesLeft]);

  const loadLevel = async () => {
    try {
      setGameState('loading');
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const url = `${backendUrl}/api/love-garden/level/${levelNumber}`;
      
      console.log('Loading level from:', url);
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      console.log('Level response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Level load error:', errorText);
        throw new Error(`Failed to load level: ${response.status}`);
      }

      const data = await response.json();
      console.log('Level data loaded:', data);
      
      setLevelConfig(data);
      setBoard(data.board_layout);
      setMovesLeft(data.moves_allowed);
      setGoals(data.goals);
      
      // Store world metadata globally for use in scoring
      window.loveGardenWorldMeta = {
        color: data.world_color,
        secondaryTiles: data.world_secondary_tiles || [],
        badge: data.world_badge
      };
      
      // Load user's XP
      try {
        console.log('🔍 Fetching XP from:', `${backendUrl}/api/love-garden/progress`);
        const progressResponse = await fetch(`${backendUrl}/api/love-garden/progress`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        console.log('📊 Progress response status:', progressResponse.status);
        
        if (progressResponse.ok) {
          const progressData = await progressResponse.json();
          console.log('✅ Progress data received:', progressData);
          console.log('💰 XP value:', progressData.xp);
          setUserXP(progressData.xp || 0);
        } else {
          console.error('❌ Progress fetch failed:', progressResponse.status, await progressResponse.text());
        }
      } catch (error) {
        console.error('❌ Error fetching XP:', error);
      }
      
      // Check if Level 1 and first time playing
      if (levelNumber === 1) {
        const hasSeenTutorial = localStorage.getItem('loveGardenTutorialSeen');
        if (!hasSeenTutorial) {
          setShowTutorial(true);
        }
      }
      
      // Check if new mechanics - show tutorial
      const newMechanics = checkForNewMechanics(data.mechanics);
      if (newMechanics) {
        setGameState('tutorial');
      } else {
        setGameState('playing');
      }
    } catch (error) {
      console.error('Error loading level:', error);
      alert(`Failed to load level: ${error.message}`);
      onClose();
    }
  };

  const checkForNewMechanics = (mechanics) => {
    // Check localStorage for seen mechanics
    const seenMechanics = JSON.parse(localStorage.getItem('seenMechanics') || '[]');
    const newMechanic = mechanics.find(m => !seenMechanics.includes(m));
    return newMechanic;
  };

  const skipTutorial = () => {
    const mechanics = levelConfig?.mechanics || [];
    const seenMechanics = JSON.parse(localStorage.getItem('seenMechanics') || '[]');
    localStorage.setItem('seenMechanics', JSON.stringify([...seenMechanics, ...mechanics]));
    setGameState('playing');
  };

  const [hoveredTile, setHoveredTile] = useState(null);

  // Hint system
  const useHint = async () => {
    if (isAnimating || !board) return;
    
    // Check if user has free hints
    if (hintsLeft > 0) {
      findAndShowHint();
      setHintsLeft(hintsLeft - 1);
    } else {
      // Need to use XP - show modal
      setShowXPConfirm(true);
    }
  };
  
  const confirmXPHint = async () => {
    const hintCost = 50;
    setShowXPConfirm(false);
    
    try {
      // Deduct XP from backend
      const response = await fetch(`${API_BASE_URL}/api/love-garden/use-hint`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ xp_cost: hintCost })
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserXP(data.xp_remaining);
        findAndShowHint();
      } else {
        const error = await response.json();
        alert(error.detail || 'Not enough XP for hint!');
      }
    } catch (error) {
      console.error('Hint error:', error);
      alert('Failed to use hint. Please try again.');
    }
  };
  
  const findAndShowHint = () => {
    // Find a valid move
    for (let row = 0; row < 8; row++) {
      for (let col = 0; col < 8; col++) {
        // Try swapping with right neighbor
        if (col < 7) {
          const newBoard = swapBoardTiles(board, row, col, row, col + 1);
          if (findMatches(newBoard).length > 0) {
            showHintAnimation(row, col, row, col + 1);
            return;
          }
        }
        
        // Try swapping with bottom neighbor
        if (row < 7) {
          const newBoard = swapBoardTiles(board, row, col, row + 1, col);
          if (findMatches(newBoard).length > 0) {
            showHintAnimation(row, col, row + 1, col);
            return;
          }
        }
      }
    }
  };
  
  const swapBoardTiles = (board, row1, col1, row2, col2) => {
    const newBoard = board.map(row => row.map(tile => ({ ...tile })));
    const temp = newBoard[row1][col1];
    newBoard[row1][col1] = { ...newBoard[row2][col2] };
    newBoard[row2][col2] = { ...temp };
    return newBoard;
  };
  
  const showHintAnimation = (row1, col1, row2, col2) => {
    setHintedTiles([
      { row: row1, col: col1 },
      { row: row2, col: col2 }
    ]);
    setShowHintGlow(true);
    
    // Clear hint after 3 seconds
    setTimeout(() => {
      setHintedTiles([]);
      setShowHintGlow(false);
    }, 3000);
  };

  const handleTileClick = (row, col) => {
    // STRICT blocking - check ref first, then state
    if (isAnimatingRef.current || isProcessingRef.current) {
      console.log('❌ BLOCKED by ref - isAnimating:', isAnimatingRef.current, 'isProcessing:', isProcessingRef.current);
      return;
    }
    
    if (isAnimating) {
      console.log('❌ BLOCKED by state - isAnimating:', isAnimating);
      return;
    }
    
    if (gameState !== 'playing') {
      console.log('❌ BLOCKED - gameState:', gameState);
      return;
    }
    
    console.log('=== TILE CLICK ALLOWED ===', { row, col });

    // Booster mode
    if (selectedBooster) {
      applyBooster(row, col);
      return;
    }

    const tile = board[row][col];
    
    // Can't select frozen tiles
    if (tile.blocker === 'frozen') {
      console.log('❌ Tile is frozen');
      return;
    }

    if (!selectedTile) {
      // First selection
      console.log('✓ Selected tile:', row, col);
      setSelectedTile({ row, col });
    } else {
      // Second selection - try to swap
      if (selectedTile.row === row && selectedTile.col === col) {
        // Deselect
        console.log('✓ Deselected tile');
        setSelectedTile(null);
      } else if (areAdjacent(selectedTile.row, selectedTile.col, row, col)) {
        // Adjacent - attempt swap
        console.log('✓ Tiles are adjacent, attempting swap');
        attemptSwap(selectedTile, { row, col });
      } else {
        // Not adjacent - select new tile
        console.log('✓ Selected new tile:', row, col);
        setSelectedTile({ row, col });
      }
    }
  };

  const attemptSwap = async (tile1, tile2) => {
    console.log('attemptSwap called');
    console.log('isAnimating:', isAnimating, 'isAnimatingRef:', isAnimatingRef.current);
    console.log('isProcessing:', isProcessingRef.current);
    
    // Prevent double-processing
    if (isAnimatingRef.current || isProcessingRef.current) {
      console.log('❌ Blocked: Already processing');
      return;
    }
    
    isAnimatingRef.current = true;
    isProcessingRef.current = true;
    setIsAnimating(true);
    setSelectedTile(null);
    
    try {
      // Swap tiles
      const newBoard = JSON.parse(JSON.stringify(board));
      const temp = newBoard[tile1.row][tile1.col];
      newBoard[tile1.row][tile1.col] = newBoard[tile2.row][tile2.col];
      newBoard[tile2.row][tile2.col] = temp;
      
      // Update positions
      newBoard[tile1.row][tile1.col].row = tile1.row;
      newBoard[tile1.row][tile1.col].col = tile1.col;
      newBoard[tile2.row][tile2.col].row = tile2.row;
      newBoard[tile2.row][tile2.col].col = tile2.col;
      
      setBoard(newBoard);
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Check for matches
      const matches = findMatches(newBoard);
      console.log('Matches found:', matches.length);
      
      if (matches.length > 0) {
        // Valid move - decrement moves
        const newMovesLeft = movesLeft - 1;
        setMovesLeft(newMovesLeft);
        console.log('Valid move! New moves left:', newMovesLeft);
        
        // Process matches (passing the new moves count and goals)
        const finalBoard = await processMatches(newBoard, matches, 0, newMovesLeft);
        // processMatches handles resetting the flags and returns final board
        
        // After ALL processing complete, check if board has possible moves
        console.log('🔍 Checking for possible moves after cascade...');
        if (finalBoard && !hasPossibleMoves(finalBoard)) {
          console.log('⚠️ No possible moves detected!');
          setShowNoMovesModal(true);
        }
      } else {
        // Invalid move - swap back with visual feedback
        console.log('Invalid move - no matches');
        
        // Show shake animation on the swapped tiles
        setInvalidSwapTile(`${tile1.row}-${tile1.col}`);
        setTimeout(() => setInvalidSwapTile(`${tile2.row}-${tile2.col}`), 50);
        
        await new Promise(resolve => setTimeout(resolve, 300));
        setBoard(board);
        
        // Clear shake animation
        setTimeout(() => setInvalidSwapTile(null), 400);
        
        // Reset flags for invalid move
        isAnimatingRef.current = false;
        isProcessingRef.current = false;
        setIsAnimating(false);
      }
    } catch (error) {
      console.error('Error in attemptSwap:', error);
      setBoard(board);
      // Reset flags on error
      isAnimatingRef.current = false;
      isProcessingRef.current = false;
      setIsAnimating(false);
    }
    // No finally block - processMatches handles flag reset for valid moves
  };

  const processMatches = async (currentBoard, matches, cascadeLevel = 0, currentMovesLeft = movesLeft, currentGoals = null) => {
    try {
      console.log('🎯 === processMatches START ===');
      console.log(`Cascade level: ${cascadeLevel}, Matches: ${matches.length}, Moves left: ${currentMovesLeft}`);
      
      if (matches.length === 0) {
        console.log('No matches, ending cascade');
        isAnimatingRef.current = false;
        isProcessingRef.current = false;
        setIsAnimating(false);
        return currentBoard;
      }
      
      // Prevent infinite cascades
      if (cascadeLevel > 10) {
        console.warn('Max cascade level reached, stopping');
        isAnimatingRef.current = false;
        isProcessingRef.current = false;
        setIsAnimating(false);
        return currentBoard;
      }
    
    // Use passed goals or fall back to state goals
    const goalsToUse = currentGoals || goals;
    console.log(`Processing matches at cascade level ${cascadeLevel}, matches: ${matches.length}`);
    console.log('Current goals:', goalsToUse);
    setIsAnimating(true);
    
    // Track combo
    if (cascadeLevel > 0) {
      setCascadeCount(cascadeLevel);
      setStats(prev => ({ ...prev, maxCascade: Math.max(prev.maxCascade, cascadeLevel) }));
    }
    if (matches.length > 1) {
      setComboCount(matches.length);
      setStats(prev => ({ ...prev, maxCombo: Math.max(prev.maxCombo, matches.length) }));
    }
    
    // Calculate points (only once per match, not on every render)
    const basePoints = matches.reduce((sum, match) => sum + (match.positions.length * 10), 0);
    const comboBonus = matches.length > 1 ? Math.min(basePoints * 0.25 * (matches.length - 1), basePoints * 2) : 0;
    const cascadeBonus = cascadeLevel > 0 ? basePoints * 0.25 * cascadeLevel : 0;
    
    // Calculate bonus XP for secondary tiles
    let bonusXP = 0;
    const secondaryTiles = window.loveGardenWorldMeta?.secondaryTiles || [];
    matches.forEach(match => {
      match.positions.forEach(pos => {
        const tile = currentBoard[pos.row][pos.col];
        const secondaryMatch = secondaryTiles.find(st => st.type === tile.type);
        if (secondaryMatch) {
          bonusXP += secondaryMatch.xp_bonus;
          console.log(`✨ Bonus XP! ${secondaryMatch.emoji} ${tile.type} = +${secondaryMatch.xp_bonus} XP`);
        }
      });
    });
    
    const totalPoints = Math.floor(basePoints + comboBonus + cascadeBonus + bonusXP);
    
    setScore(prev => prev + totalPoints);
    
    // Clear matched tiles and update goals
    let newBoard = JSON.parse(JSON.stringify(currentBoard));
    let goalsUpdated = JSON.parse(JSON.stringify(goalsToUse)); // Deep copy to avoid mutations
    let blockersCleared = 0;
    let collectiblesFound = {}; // Track all collectible types
    
    console.log('🔍 Checking tiles in matches...');
    
    // First pass: count goals BEFORE clearing tiles
    matches.forEach(match => {
      match.positions.forEach(pos => {
        const tile = newBoard[pos.row][pos.col];
        console.log(`Tile at ${pos.row},${pos.col}: type=${tile.type}, blocker=${tile.blocker}`);
        
        // Count collectibles (hearts, lotus, stars, gems, butterflies, rainbows)
        if (tile.type && tile.type !== 'empty') {
          collectiblesFound[tile.type] = (collectiblesFound[tile.type] || 0) + 1;
        }
        
        // Count blockers
        if (tile.blocker) {
          blockersCleared++;
        }
      });
    });
    
    console.log('Collectibles found:', collectiblesFound);
    
    // Update goals with collected items
    goalsUpdated = goalsUpdated.map(goal => {
      // Handle collection goals (collect_heart, collect_lotus, collect_star, etc.)
      if (goal.type && goal.type.startsWith('collect_')) {
        let collectibleType = goal.type.replace('collect_', '');
        // Backend returns singular (collect_heart), but handle plural too for old levels (collect_hearts)
        if (collectibleType.endsWith('s') && collectibleType !== 'lotus') {
          collectibleType = collectibleType.slice(0, -1);
        }
        const collected = collectiblesFound[collectibleType] || 0;
        const newCurrent = Math.min(goal.current + collected, goal.count);
        const emoji = goal.emoji || '💗';
        console.log(`${emoji} Updating ${collectibleType}: ${goal.current} + ${collected} = ${newCurrent} / ${goal.count}`);
        return { ...goal, current: newCurrent };
      }
      if (goal.type === 'clear_blockers') {
        const newCurrent = Math.min(goal.current + blockersCleared, goal.count);
        console.log(`🌿 Updating blockers: ${goal.current} + ${blockersCleared} = ${newCurrent} / ${goal.count}`);
        return { ...goal, current: newCurrent };
      }
      return goal;
    });
    
    console.log('Updated goals:', goalsUpdated);
    
    // Second pass: clear the tiles
    matches.forEach(match => {
      match.positions.forEach(pos => {
        const tile = newBoard[pos.row][pos.col];
        
        // Clear tile
        tile.type = 'empty';
        tile.is_special = false;
        tile.special_type = null;
        
        // Handle blockers
        if (tile.blocker) {
          tile.blocker_health = Math.max(0, (tile.blocker_health || 0) - 1);
          if (tile.blocker_health === 0) {
            tile.blocker = null;
          }
        }
      });
    });
    
    setGoals(goalsUpdated);
    if (blockersCleared > 0) {
      setStats(prev => ({ ...prev, blockersCleared: prev.blockersCleared + blockersCleared }));
    }
    
    setBoard(newBoard);
    console.log('⏱️ Waiting 200ms for tile clear animation...');
    await new Promise(resolve => setTimeout(resolve, 200));
    
    // Apply gravity
    console.log('🌊 Applying gravity...');
    newBoard = applyGravity(newBoard);
    setBoard(newBoard);
    console.log('⏱️ Waiting 300ms for gravity animation...');
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Check for new matches (cascade)
    console.log('🔍 Checking for cascade matches...');
    const newMatches = findMatches(newBoard);
    if (newMatches.length > 0) {
      console.log(`✨ Cascade continues with ${newMatches.length} new matches...`);
      // Pass updated goals to next cascade level
      return await processMatches(newBoard, newMatches, cascadeLevel + 1, currentMovesLeft, goalsUpdated);
    }
    
    // No more cascades - animation done
    console.log('✅ Cascade complete, resetting animation flags');
    isAnimatingRef.current = false;
    isProcessingRef.current = false;
    setIsAnimating(false);
    
    // Check win/lose after all animations complete
    console.log('🏁 Checking win/lose...');
    checkWinLose(goalsUpdated, currentMovesLeft);
    
    // Check for possible moves after cascade completes
    console.log('🔍 Final check for possible moves...');
    const hasMoves = hasPossibleMoves(newBoard);
    console.log(`Possible moves available: ${hasMoves}`);
    if (!hasMoves && currentMovesLeft > 0 && !goalsUpdated.every(g => g.current >= g.count)) {
      console.log('⚠️ No possible moves! Showing shuffle modal...');
      setTimeout(() => setShowNoMovesModal(true), 500);
    }
    
    console.log('✅ processMatches complete');
    return newBoard;
    
    } catch (error) {
      console.error('❌❌❌ CRITICAL ERROR in processMatches:', error);
      console.error('Stack:', error.stack);
      // ALWAYS reset flags on error
      isAnimatingRef.current = false;
      isProcessingRef.current = false;
      setIsAnimating(false);
      return currentBoard;
    }
  };

  const applyBooster = async (row, col) => {
    if (boosters[selectedBooster] <= 0) {
      setSelectedBooster(null);
      return;
    }
    
    setIsAnimating(true);
    let newBoard = JSON.parse(JSON.stringify(board));
    
    if (selectedBooster === 'sunbeam') {
      // Clear one tile
      newBoard[row][col].type = 'empty';
      newBoard[row][col].blocker = null;
      newBoard[row][col].blocker_health = 0;
    } else if (selectedBooster === 'waterfall') {
      // Clear column
      for (let r = 0; r < 8; r++) {
        newBoard[r][col].type = 'empty';
        if (newBoard[r][col].blocker) {
          newBoard[r][col].blocker_health = Math.max(0, (newBoard[r][col].blocker_health || 0) - 1);
          if (newBoard[r][col].blocker_health === 0) {
            newBoard[r][col].blocker = null;
          }
        }
      }
    } else if (selectedBooster === 'breeze') {
      // Shuffle board
      const tiles = [];
      for (let r = 0; r < 8; r++) {
        for (let c = 0; c < 8; c++) {
          if (newBoard[r][c].type !== 'empty' && !newBoard[r][c].blocker) {
            tiles.push(newBoard[r][c].type);
          }
        }
      }
      
      // Fisher-Yates shuffle
      for (let i = tiles.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
      }
      
      let tileIndex = 0;
      for (let r = 0; r < 8; r++) {
        for (let c = 0; c < 8; c++) {
          if (newBoard[r][c].type !== 'empty' && !newBoard[r][c].blocker) {
            newBoard[r][c].type = tiles[tileIndex++];
          }
        }
      }
    }
    
    setBoosters(prev => ({ ...prev, [selectedBooster]: prev[selectedBooster] - 1 }));
    setSelectedBooster(null);
    
    setBoard(newBoard);
    await new Promise(resolve => setTimeout(resolve, 300));
    
    newBoard = applyGravity(newBoard);
    setBoard(newBoard);
    await new Promise(resolve => setTimeout(resolve, 400));
    
    const matches = findMatches(newBoard);
    if (matches.length > 0) {
      await processMatches(newBoard, matches);
    }
    
    setIsAnimating(false);
  };

  const shuffleBoard = async () => {
    console.log('🔄 Shuffling board...');
    setIsAnimating(true);
    setShowNoMovesModal(false);
    
    let newBoard = JSON.parse(JSON.stringify(board));
    
    // Collect all non-empty, non-blocker tiles
    const tiles = [];
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        if (newBoard[r][c].type !== 'empty' && !newBoard[r][c].blocker) {
          tiles.push(newBoard[r][c].type);
        }
      }
    }
    
    // Fisher-Yates shuffle
    for (let i = tiles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [tiles[i], tiles[j]] = [tiles[j], tiles[i]];
    }
    
    // Reassign tiles to board
    let tileIndex = 0;
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        if (newBoard[r][c].type !== 'empty' && !newBoard[r][c].blocker) {
          newBoard[r][c].type = tiles[tileIndex++];
        }
      }
    }
    
    setBoard(newBoard);
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check if shuffle created any matches (shouldn't, but handle it)
    const matches = findMatches(newBoard);
    if (matches.length > 0) {
      await processMatches(newBoard, matches, 0, movesLeft);
    } else {
      setIsAnimating(false);
    }
    
    console.log('✅ Board shuffled!');
  };

  const checkWinLose = (currentGoals, movesRemaining) => {
    console.log('Checking win/lose:', { currentGoals, movesRemaining });
    
    // Check win
    const allGoalsComplete = currentGoals.every(goal => goal.current >= goal.count);
    if (allGoalsComplete) {
      console.log('WIN!');
      handleWin();
      return;
    }
    
    // Check lose - only if moves are actually exhausted
    if (movesRemaining <= 0) {
      console.log('LOSE - no moves left');
      handleLose();
      return;
    }
    
    console.log('Game continues');
  };

  const handleWin = async () => {
    console.log('🎉 LEVEL WON - Setting game state to won');
    setGameState('won');
    
    // Calculate final score with bonuses
    let finalScore = score;
    finalScore += movesLeft * 50; // Leftover moves bonus
    
    const duration = Date.now() - startTime.current;
    
    console.log('📊 Final score:', finalScore, 'Stats:', stats);
    
    // Submit to backend (but don't wait for it)
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${backendUrl}/api/love-garden/complete`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        level_number: levelNumber,
        result: 'win',
        score: finalScore,
        moves_used: levelConfig.moves_allowed - movesLeft,
        moves_remaining: movesLeft,
        goals_progress: goals,
        max_combo: stats.maxCombo,
        max_cascade: stats.maxCascade,
        specials_created: stats.specialsCreated,
        specials_used: stats.specialsUsed,
        blockers_cleared: stats.blockersCleared,
        duration_ms: duration
      })
    }).catch(error => {
      console.error('Error submitting win:', error);
    });
    
    // Modal will stay visible - user must click button to proceed
    console.log('✅ Victory modal should now be visible');
  };

  const handleLose = async () => {
    setGameState('lost');
    
    const duration = Date.now() - startTime.current;
    
    // Submit to backend
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await fetch(`${backendUrl}/api/love-garden/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          level_number: levelNumber,
          result: 'lose',
          score,
          moves_used: levelConfig.moves_allowed,
          moves_remaining: 0,
          goals_progress: goals,
          max_combo: stats.maxCombo,
          max_cascade: stats.maxCascade,
          specials_created: stats.specialsCreated,
          specials_used: stats.specialsUsed,
          blockers_cleared: stats.blockersCleared,
          duration_ms: duration
        })
      });
    } catch (error) {
      console.error('Error submitting loss:', error);
    }
  };

  const handleRestart = () => {
    loadLevel();
    setScore(0);
    setSelectedTile(null);
    setComboCount(0);
    setCascadeCount(0);
    setStats({ maxCombo: 0, maxCascade: 0, specialsCreated: 0, specialsUsed: 0, blockersCleared: 0 });
    startTime.current = Date.now();
  };

  // Render tile
  const renderTile = (tile, row, col) => {
    const isSelected = selectedTile && selectedTile.row === row && selectedTile.col === col;
    const isInvalidSwap = invalidSwapTile === `${row}-${col}`;
    const isHinted = hintedTiles.some(h => h.row === row && h.col === col);
    const tileEmojis = {
      // Heart's Awakening
      heart: '💗',
      flower: '🌺',
      butterfly: '🦋',
      // Inner Peace
      lotus: '🌸',
      droplet: '💧',
      feather: '🪶',
      // Starlit Dreams
      star: '⭐',
      moon: '🌙',
      cloud: '☁️',
      // Crystal Clarity
      gem: '💎',
      lightray: '☀️',
      bluegem: '🔷',
      // Transformation Garden
      leaf: '🍃',
      petal: '🌸',
      // Eternal Bloom
      rose: '🌹',
      sparkle: '✨',
      sun: '☀️'
    };
    
    const blockerEmojis = {
      weed: '🌿',
      stone: '🪨',
      vine: '🌿',
      frozen: '❄️',
      seed_crate: '📦'
    };

    return (
      <button
        key={`${row}-${col}`}
        onClick={() => {
          if (!isAnimating && !isAnimatingRef.current && !isProcessingRef.current) {
            handleTileClick(row, col);
          } else {
            console.log('🚫 Button click blocked in onClick handler');
          }
        }}
        disabled={isAnimating || isAnimatingRef.current || isProcessingRef.current}
        className={`
          relative w-full aspect-square rounded-lg transition-all duration-200
          flex items-center justify-center text-3xl
          ${tile.type === 'empty' ? 'bg-rose-100/50' : 'bg-white shadow-md hover:shadow-lg'}
          ${isSelected ? 'ring-4 ring-rose-500 scale-110' : ''}
          ${tile.is_special ? 'animate-pulse ring-2 ring-yellow-400' : ''}
          ${tile.blocker === 'frozen' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
          ${(isAnimating || isAnimatingRef.current || isProcessingRef.current) ? 'opacity-50 pointer-events-none' : ''}
          ${isInvalidSwap ? 'animate-shake ring-2 ring-red-500' : ''}
          ${isHinted ? 'animate-pulse ring-4 ring-yellow-400 shadow-[0_0_20px_rgba(234,179,8,0.6)]' : ''}
        `}
      >
        {tile.blocker && (
          <div className="absolute inset-0 flex items-center justify-center text-2xl z-10">
            {blockerEmojis[tile.blocker]}
            {tile.blocker_health > 1 && (
              <span className="absolute bottom-1 right-1 text-xs bg-gray-800 text-white rounded-full w-4 h-4 flex items-center justify-center">
                {tile.blocker_health}
              </span>
            )}
          </div>
        )}
        {tile.type !== 'empty' && (
          <span className={tile.blocker ? 'opacity-40' : ''}>
            {tileEmojis[tile.type] || '?'}
          </span>
        )}
        {tile.is_special && (
          <Sparkles className="absolute top-0 right-0 w-4 h-4 text-yellow-500" />
        )}
      </button>
    );
  };

  if (!board || !levelConfig) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="p-8">
          <div className="text-4xl mb-4 text-center">🌸</div>
          <p className="text-gray-600">Loading level...</p>
        </Card>
      </div>
    );
  }

  // Tutorial overlay
  if (gameState === 'tutorial') {
    const newMechanic = checkForNewMechanics(levelConfig.mechanics);
    const tutorialText = {
      weeds: "🌿 Weeds block growth. Match next to them to clear!",
      stones2: "🪨 Stones are tough! Match next to them twice to break them.",
      stones3: "🪨 Strong stones need three matches to break!",
      vines: "🌿 Vines spread! Clear them quickly before they take over.",
      frozen: "❄️ Frozen tiles can't be moved. Match them in place to thaw!",
      seed_crates: "📦 Drop seed crates to the bottom by matching beneath them!"
    };

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <Card className="p-8 max-w-md text-center">
          <div className="text-6xl mb-4">✨</div>
          <h2 className="text-2xl font-bold mb-4">New Challenge!</h2>
          <p className="text-lg mb-6">{tutorialText[newMechanic] || "New mechanic unlocked!"}</p>
          <div className="flex gap-4">
            <Button onClick={skipTutorial} variant="outline" className="flex-1">
              Got it!
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Win/Lose screens
  if (gameState === 'won') {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h2 className="text-3xl font-bold mb-2">You Bloomed!</h2>
          <p className="text-gray-600 mb-6">Level {levelNumber} Complete</p>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-rose-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-rose-600">{score}</div>
              <div className="text-xs text-gray-600">Score</div>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-purple-600">{movesLeft}</div>
              <div className="text-xs text-gray-600">Moves Left</div>
            </div>
            <div className="bg-pink-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-pink-600">{stats.maxCombo}</div>
              <div className="text-xs text-gray-600">Best Combo</div>
            </div>
            <div className="bg-amber-50 p-4 rounded-lg">
              <div className="text-2xl font-bold text-amber-600">{stats.maxCascade}</div>
              <div className="text-xs text-gray-600">Max Cascade</div>
            </div>
          </div>
          
          <Button 
            onClick={() => {
              console.log('👆 User clicked Continue Growing button');
              console.log('Calling onComplete with:', { won: true, score, stats });
              onComplete({ won: true, score, stats });
              console.log('Calling onClose to return to map');
              onClose();
            }} 
            className="w-full bg-gradient-to-r from-rose-500 to-pink-600"
          >
            Continue Growing 🌸
          </Button>
        </Card>
      </div>
    );
  }

  if (gameState === 'lost') {
    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">🌱</div>
          <h2 className="text-2xl font-bold mb-2">Every bloom takes time</h2>
          <p className="text-gray-600 mb-6">Don't give up on your garden!</p>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <div className="text-2xl font-bold text-gray-700">{score}</div>
            <div className="text-xs text-gray-600">Score This Attempt</div>
          </div>
          
          <div className="flex gap-4">
            <Button onClick={handleRestart} className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600">
              Try Again
            </Button>
            <Button onClick={onClose} variant="outline" className="flex-1">
              Back to Map
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main game view
  const worldColor = levelConfig?.world_color || '#FFB6C1';
  const bgStyle = {
    background: `linear-gradient(135deg, ${worldColor}15, ${worldColor}30, ${worldColor}15)`
  };
  
  return (
    <div className="fixed inset-0 z-40 overflow-auto pt-16" style={bgStyle}>
      {/* No Moves Modal */}
      {showNoMovesModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <Card className="p-8 max-w-md w-full text-center">
            <div className="text-6xl mb-4">🔄</div>
            <h2 className="text-2xl font-bold mb-2">No Possible Moves!</h2>
            <p className="text-gray-600 mb-6">
              The board has no valid swaps left. Let's shuffle the tiles for you!
            </p>
            <div className="flex gap-4">
              <Button
                onClick={shuffleBoard}
                className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600"
              >
                <Shuffle className="w-4 h-4 mr-2" />
                Shuffle Board (Free)
              </Button>
            </div>
          </Card>
        </div>
      )}
      
      <div className="max-w-2xl mx-auto p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <Button onClick={onClose} variant="ghost" size="sm">
            <X className="w-5 h-5" />
          </Button>
          <div className="text-center">
            <div className="text-sm text-gray-600">Level {levelNumber}</div>
            <div className="font-bold">{levelConfig.world_tag}</div>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setShowHowToPlay(true)} 
              variant="ghost" 
              size="sm"
              className="text-purple-600"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            {isAnimating && (
              <Button 
                onClick={() => { 
                  isAnimatingRef.current = false;
                  isProcessingRef.current = false;
                  setIsAnimating(false); 
                  setForceRender(prev => prev + 1);
                  console.log('🔓 Manually unstuck'); 
                }} 
                variant="ghost" 
                size="sm" 
                className="text-red-500"
              >
                Unstuck
              </Button>
            )}
            <Button onClick={handleRestart} variant="ghost" size="sm">
              <RotateCcw className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Goals & Stats */}
        <Card className="p-4 mb-4" key={`goals-${goals[0]?.current || 0}`}>
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-rose-600">{movesLeft}</div>
              <div className="text-xs text-gray-600">Moves</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-purple-600">{score}</div>
              <div className="text-xs text-gray-600">Score</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-amber-600">{userXP}</div>
              <div className="text-xs text-gray-600">XP</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-pink-600">
                {goals[0]?.current ?? 0}/{goals[0]?.count ?? 0}
              </div>
              <div className="text-xs text-gray-600">
                {goals[0]?.emoji || '💗'} {goals[0]?.name || 'Hearts'}
              </div>
            </div>
          </div>
          
          {goals.length > 1 && (
            <div className="mt-4 pt-4 border-t text-center">
              <div className="text-sm text-gray-600">
                {goals[1].type === 'clear_blockers' && `Clear ${goals[1].current ?? 0}/${goals[1].count ?? 0} Blockers`}
                {goals[1].type === 'drop_seeds' && `Drop ${goals[1].current ?? 0}/${goals[1].count ?? 0} Seeds`}
              </div>
            </div>
          )}
          
          {(comboCount > 1 || cascadeCount > 0) && (
            <div className="mt-4 flex justify-center gap-4">
              {comboCount > 1 && (
                <div className="text-sm font-bold text-amber-600 animate-bounce">
                  🔥 {comboCount}x Combo!
                </div>
              )}
              {cascadeCount > 0 && (
                <div className="text-sm font-bold text-blue-600 animate-bounce">
                  ⚡ {cascadeCount}x Cascade!
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Hint Button */}
        <div className="flex justify-center mb-4">
          <Button
            onClick={useHint}
            disabled={isAnimating}
            className={`bg-gradient-to-r from-yellow-400 to-amber-500 text-white font-bold px-6 py-3 rounded-full shadow-lg ${
              showHintGlow ? 'animate-pulse ring-4 ring-yellow-300' : ''
            }`}
          >
            <span className="text-2xl mr-2">💡</span>
            Hint ({hintsLeft} free)
            {hintsLeft === 0 && <span className="ml-2 text-xs">(50 XP)</span>}
          </Button>
        </div>

        {/* Board */}
        <Card className="p-4 mb-4" ref={boardRef}>
          <div className="grid grid-cols-8 gap-1">
            {board.map((row, rowIdx) =>
              row.map((tile, colIdx) => renderTile(tile, rowIdx, colIdx))
            )}
          </div>
        </Card>

        {/* Boosters */}
        {(boosters.sunbeam > 0 || boosters.waterfall > 0 || boosters.breeze > 0) && (
          <Card className="p-4">
            <div className="flex justify-center gap-4">
              {boosters.sunbeam > 0 && (
                <Button
                  onClick={() => setSelectedBooster(selectedBooster === 'sunbeam' ? null : 'sunbeam')}
                  className={`flex-col h-auto py-2 ${selectedBooster === 'sunbeam' ? 'ring-2 ring-yellow-500' : ''}`}
                  variant={selectedBooster === 'sunbeam' ? 'default' : 'outline'}
                >
                  <Sun className="w-6 h-6 mb-1" />
                  <span className="text-xs">{boosters.sunbeam}</span>
                </Button>
              )}
              {boosters.waterfall > 0 && (
                <Button
                  onClick={() => setSelectedBooster(selectedBooster === 'waterfall' ? null : 'waterfall')}
                  className={`flex-col h-auto py-2 ${selectedBooster === 'waterfall' ? 'ring-2 ring-blue-500' : ''}`}
                  variant={selectedBooster === 'waterfall' ? 'default' : 'outline'}
                >
                  <Droplets className="w-6 h-6 mb-1" />
                  <span className="text-xs">{boosters.waterfall}</span>
                </Button>
              )}
              {boosters.breeze > 0 && (
                <Button
                  onClick={() => setSelectedBooster(selectedBooster === 'breeze' ? null : 'breeze')}
                  className={`flex-col h-auto py-2 ${selectedBooster === 'breeze' ? 'ring-2 ring-green-500' : ''}`}
                  variant={selectedBooster === 'breeze' ? 'default' : 'outline'}
                >
                  <Wind className="w-6 h-6 mb-1" />
                  <span className="text-xs">{boosters.breeze}</span>
                </Button>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Level 1 Tutorial */}
      {showTutorial && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4">
          <Card className="p-6 max-w-xl w-full">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🌸</div>
              <h2 className="text-3xl font-bold text-rose-600 mb-2">Welcome to Love Garden!</h2>
              <p className="text-gray-700">Let's learn how to play this self-love match-3 game</p>
            </div>

            <div className="space-y-4 mb-6">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-bold text-purple-600 mb-2">🎯 Your Goal</h3>
                <p className="text-gray-700 text-sm">Match 3 or more tiles to collect them. Complete all goals before running out of moves!</p>
              </div>

              <div className="bg-pink-50 p-4 rounded-lg">
                <h3 className="font-bold text-pink-600 mb-2">💫 How to Play</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• <strong>Tap a tile</strong>, then tap an adjacent tile to swap</li>
                  <li>• Swaps only work if they create a match of 3+</li>
                  <li>• Watch for cascades and combos for bonus points!</li>
                </ul>
              </div>

              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-bold text-amber-600 mb-2">✨ Earn XP to Unlock Hints</h3>
                <ul className="text-gray-700 text-sm space-y-1">
                  <li>• Complete levels to earn XP points</li>
                  <li>• Get 1 free hint per level</li>
                  <li>• Buy more hints for 50 XP each</li>
                  <li>• Use hints when you're stuck!</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-4">
              <Button 
                onClick={() => {
                  localStorage.setItem('loveGardenTutorialSeen', 'true');
                  setShowTutorial(false);
                }}
                className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white"
              >
                Let's Play! 🌸
              </Button>
              <Button 
                onClick={() => {
                  localStorage.setItem('loveGardenTutorialSeen', 'true');
                  setShowTutorial(false);
                  setShowHowToPlay(true);
                }}
                variant="outline"
                className="flex-1"
              >
                Full Guide
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* XP Hint Confirmation Modal */}
      {showXPConfirm && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
          <Card className="p-6 max-w-md w-full">
            <div className="text-center mb-6">
              <div className="text-5xl mb-4">💡</div>
              <h2 className="text-2xl font-bold text-amber-600 mb-2">Use XP for Hint?</h2>
              <p className="text-gray-700 mb-4">You've used your free hint for this level.</p>
              
              <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 rounded-lg mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-gray-700">Hint Cost:</span>
                  <span className="text-xl font-bold text-amber-600">50 XP</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Your XP:</span>
                  <span className="text-xl font-bold text-purple-600">{userXP} XP</span>
                </div>
                {userXP < 50 && (
                  <div className="mt-2 text-red-600 text-sm font-semibold">
                    ⚠️ Not enough XP! Complete levels to earn more.
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-500">
                💝 Earn XP by completing levels to unlock more hints!
              </p>
            </div>

            <div className="flex gap-4">
              <Button 
                onClick={() => setShowXPConfirm(false)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmXPHint}
                disabled={userXP < 50}
                className="flex-1 bg-gradient-to-r from-amber-500 to-yellow-600 text-white disabled:opacity-50"
              >
                {userXP >= 50 ? 'Use 50 XP' : 'Not Enough XP'}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* How to Play Modal */}
      {showHowToPlay && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <Card className="p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-rose-600">How to Play Love Garden 🌸</h2>
              <button onClick={() => setShowHowToPlay(false)} className="text-gray-500 hover:text-gray-700">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Basic Gameplay */}
              <div>
                <h3 className="text-lg font-bold text-purple-600 mb-2">🎯 Goal</h3>
                <p className="text-gray-700">
                  Match 3 or more tiles of the same type to collect them and complete level goals within the move limit.
                </p>
              </div>

              {/* How to Match */}
              <div>
                <h3 className="text-lg font-bold text-purple-600 mb-2">💫 How to Match</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Tap a tile</strong>, then tap an adjacent tile to swap them</li>
                  <li>• Swaps only work if they create a match of 3+ tiles</li>
                  <li>• Match <strong>3 in a row</strong> (horizontal or vertical)</li>
                  <li>• Bigger matches = more points!</li>
                </ul>
              </div>

              {/* Goals */}
              <div>
                <h3 className="text-lg font-bold text-purple-600 mb-2">🎮 Level Goals</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Collect tiles:</strong> Match specific types (💗 Hearts, 🌸 Petals, etc.)</li>
                  <li>• <strong>Clear blockers:</strong> Match tiles next to 🪨 stones or 🌿 weeds</li>
                  <li>• <strong>Drop seeds:</strong> Match below 📦 crates to drop them to the bottom</li>
                  <li>• Complete all goals before running out of moves!</li>
                </ul>
              </div>

              {/* Special Tiles */}
              <div>
                <h3 className="text-lg font-bold text-purple-600 mb-2">✨ Special Tiles</h3>
                <p className="text-gray-700 mb-2">Match 4+ tiles to create special pieces:</p>
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Match 4:</strong> Creates line-clearing tile (clears row/column)</li>
                  <li>• <strong>Match 5:</strong> Creates bomb tile (clears 3×3 area)</li>
                  <li>• Special tiles have ✨ sparkles and glow</li>
                  <li>• Match them like normal tiles to activate their power!</li>
                </ul>
              </div>

              {/* Cascades & Combos */}
              <div>
                <h3 className="text-lg font-bold text-purple-600 mb-2">🔥 Cascades & Combos</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Cascades:</strong> When tiles fall and create new matches automatically</li>
                  <li>• <strong>Combos:</strong> Multiple matches from one move = bonus points!</li>
                  <li>• Look for <strong>⚡ Cascade!</strong> and <strong>🔥 Combo!</strong> indicators</li>
                </ul>
              </div>

              {/* Hints */}
              <div>
                <h3 className="text-lg font-bold text-purple-600 mb-2">💡 Hints</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• Get <strong>1 free hint</strong> per level</li>
                  <li>• Hints show you a valid move with glowing tiles</li>
                  <li>• Additional hints cost <strong>50 XP</strong> each</li>
                  <li>• Use hints when you're stuck!</li>
                </ul>
              </div>

              {/* Tips */}
              <div className="bg-gradient-to-r from-rose-50 to-pink-50 p-4 rounded-lg">
                <h3 className="text-lg font-bold text-rose-600 mb-2">💝 Pro Tips</h3>
                <ul className="space-y-1 text-gray-700 text-sm">
                  <li>• Plan ahead - look for moves that create cascades</li>
                  <li>• Clear blockers early (they don't fall like tiles)</li>
                  <li>• Save special tiles for when you really need them</li>
                  <li>• Match at the bottom - creates more cascades</li>
                  <li>• Don't rush - think before each move!</li>
                </ul>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <Button 
                onClick={() => setShowHowToPlay(false)}
                className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-8"
              >
                Got It! Let's Play 🌸
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default LoveGardenGame;
